import { useEffect, useState, useCallback } from 'react';
import { Copy, RefreshCw } from 'lucide-react';
import { useCanvasStore } from '../../stores/canvasStore';
import { useStudioStore } from '../../stores/studioStore';
import { useMappingStore } from '../../stores/mappingStore';
import { resolveMappedValueFromBrandData } from '../../lib/mappingResolver';
import type { EnhancedFieldMapping } from '../../types/mapping';

interface ButtonColorReading {
  elementId: string;
  elementName: string;
  elementType: string;
  storeBackgroundColor: string | undefined;

  editAsBrandId: string | null;
  editAsBrandDataPresent: boolean;
  editAsBrandDataKeyCount: number;
  smSchoolsKeys: string[];
  smSchoolsPrimaryColor: string | null | undefined;

  hasMappingsForElement: boolean;
  allMappingsForElement: Array<{
    targetProperty: string;
    source: string;
    tableName: string;
    columnName: string;
    fallbackValue: string | null;
  }>;
  buttonFillColorMapping: {
    targetProperty: string;
    source: string;
    tableName: string;
    columnName: string;
    fallbackValue: string | null;
  } | null;

  constructedLookupKey: string | null;
  lookupKeyFoundInBrandData: boolean;
  lookupKeyValue: string | null | undefined;
  resolvedValue: string | null;
  finalFillWillBe: string;

  fabricGroupFound: boolean;
  fabricBgRefPresent: boolean;
  fabricBgFillLive: string | null;
  fabricBgMatchesResolved: boolean;

  verdict: string;
  verdictSeverity: 'ok' | 'warning' | 'error';
}

const ELEMENT_ID_KEY = '_csElementId';

function readButtonColorDiagnostic(): ButtonColorReading | null {
  const fc = useCanvasStore.getState().fabricCanvas;
  if (!fc) return null;
  const active = fc.getActiveObject();
  if (!active) return null;

  const elementId = (active as unknown as Record<string, unknown>)[ELEMENT_ID_KEY] as string | undefined;
  if (!elementId) return null;

  const storeState = useStudioStore.getState();
  const storeEl = storeState.canvasState.elements.find((e) => e.id === elementId);
  if (!storeEl || storeEl.type !== 'button') return null;

  const buttonEl = storeEl as typeof storeEl & {
    backgroundColor?: string;
    name?: string;
  };

  const editAsBrandId = storeState.editAsBrandId;
  const editAsBrandData = storeState.editAsBrandData ?? {};
  const editAsBrandDataKeys = Object.keys(editAsBrandData);
  const smSchoolsKeys = editAsBrandDataKeys.filter((k) => k.startsWith('sm.sm_schools.'));
  const smSchoolsPrimaryColor = editAsBrandData['sm.sm_schools.primary_color'] as string | null | undefined;

  const mappingState = useMappingStore.getState();
  const allMappings = mappingState.elementMappings[elementId] ?? [];
  const buttonFillColorMappingRaw = allMappings.find(
    (m) => m.targetProperty === 'buttonFillColor'
  );

  const buttonFillColorMapping = buttonFillColorMappingRaw
    ? {
        targetProperty: buttonFillColorMappingRaw.targetProperty,
        source: buttonFillColorMappingRaw.source,
        tableName: buttonFillColorMappingRaw.tableName,
        columnName: buttonFillColorMappingRaw.columnName,
        fallbackValue: buttonFillColorMappingRaw.fallbackValue,
      }
    : null;

  let constructedLookupKey: string | null = null;
  let lookupKeyFoundInBrandData = false;
  let lookupKeyValue: string | null | undefined = null;
  let resolvedValue: string | null = null;

  if (buttonFillColorMappingRaw) {
    constructedLookupKey = `${buttonFillColorMappingRaw.source}.${buttonFillColorMappingRaw.tableName}.${buttonFillColorMappingRaw.columnName}`;
    lookupKeyFoundInBrandData = constructedLookupKey in editAsBrandData;
    lookupKeyValue = editAsBrandData[constructedLookupKey] as string | null | undefined;
    try {
      resolvedValue = resolveMappedValueFromBrandData(
        buttonFillColorMappingRaw as EnhancedFieldMapping,
        editAsBrandData
      );
    } catch {
      resolvedValue = null;
    }
  }

  const finalFillWillBe = resolvedValue ?? buttonEl.backgroundColor ?? '(no fill)';

  // Fabric object state — _csButtonBg is a Rect REFERENCE stored on the
  // FabricGroup itself (fabricFactory.ts:435), NOT a boolean on a child.
  // This matches the lookup used by fabricSync.ts:240.
  let fabricBgFillLive: string | null = null;
  let fabricGroupFound = false;
  let fabricBgRefPresent = false;
  try {
    const group = active as unknown as {
      _objects?: unknown[];
      _csButtonBg?: { fill?: unknown };
      _csIsButton?: boolean;
    };
    if (group._objects && Array.isArray(group._objects)) {
      fabricGroupFound = true;
    }
    const bgRef = group._csButtonBg;
    if (bgRef) {
      fabricBgRefPresent = true;
      if (typeof bgRef.fill === 'string') {
        fabricBgFillLive = bgRef.fill;
      }
    }
  } catch {
    // ignore
  }

  const fabricBgMatchesResolved =
    fabricBgFillLive !== null &&
    resolvedValue !== null &&
    fabricBgFillLive.toLowerCase() === resolvedValue.toLowerCase();

  let verdict = '';
  let verdictSeverity: 'ok' | 'warning' | 'error' = 'ok';

  if (!editAsBrandId) {
    verdict = 'Edit-As is OFF -- resolution not attempted';
    verdictSeverity = 'warning';
  } else if (!buttonFillColorMapping) {
    verdict = 'No buttonFillColor mapping exists for this element';
    verdictSeverity = 'warning';
  } else if (!lookupKeyFoundInBrandData) {
    verdict = `Bug: lookup key "${constructedLookupKey}" not found in editAsBrandData (school data may not be loaded)`;
    verdictSeverity = 'error';
  } else if (resolvedValue === null) {
    verdict = 'Bug: resolveMappedValueFromBrandData returned null despite key present';
    verdictSeverity = 'error';
  } else if (!fabricBgRefPresent) {
    verdict = 'Bug: _csButtonBg reference missing on Fabric group -- reference lost or object not created via factory';
    verdictSeverity = 'error';
  } else if (fabricBgFillLive === null) {
    verdict = 'Bug: _csButtonBg reference present but fill is not a string';
    verdictSeverity = 'error';
  } else if (!fabricBgMatchesResolved) {
    verdict = `Bug: resolved value is "${resolvedValue}" but Fabric fill is "${fabricBgFillLive}" -- write-back failing`;
    verdictSeverity = 'error';
  } else {
    verdict = 'OK: resolution chain intact and Fabric fill matches resolved value';
    verdictSeverity = 'ok';
  }

  return {
    elementId,
    elementName: buttonEl.name ?? '(unnamed)',
    elementType: storeEl.type,
    storeBackgroundColor: buttonEl.backgroundColor,

    editAsBrandId,
    editAsBrandDataPresent: editAsBrandDataKeys.length > 0,
    editAsBrandDataKeyCount: editAsBrandDataKeys.length,
    smSchoolsKeys,
    smSchoolsPrimaryColor,

    hasMappingsForElement: allMappings.length > 0,
    allMappingsForElement: allMappings.map((m) => ({
      targetProperty: m.targetProperty,
      source: m.source,
      tableName: m.tableName,
      columnName: m.columnName,
      fallbackValue: m.fallbackValue,
    })),
    buttonFillColorMapping,

    constructedLookupKey,
    lookupKeyFoundInBrandData,
    lookupKeyValue,
    resolvedValue,
    finalFillWillBe,

    fabricGroupFound,
    fabricBgRefPresent,
    fabricBgFillLive,
    fabricBgMatchesResolved,

    verdict,
    verdictSeverity,
  };
}

export function ButtonColorDiagnostic() {
  const [reading, setReading] = useState<ButtonColorReading | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');
  const fabricCanvas = useCanvasStore((s) => s.fabricCanvas);

  const refresh = useCallback(() => {
    setReading(readButtonColorDiagnostic());
  }, []);

  useEffect(() => {
    if (!fabricCanvas) return;

    const handleSelection = () => {
      requestAnimationFrame(() => refresh());
    };
    const handleCleared = () => setReading(null);

    fabricCanvas.on('selection:created', handleSelection);
    fabricCanvas.on('selection:updated', handleSelection);
    fabricCanvas.on('selection:cleared', handleCleared);

    refresh();

    return () => {
      fabricCanvas.off('selection:created', handleSelection);
      fabricCanvas.off('selection:updated', handleSelection);
      fabricCanvas.off('selection:cleared', handleCleared);
    };
  }, [fabricCanvas, refresh]);

  const handleCopy = useCallback(async () => {
    if (!reading) return;
    const payload = {
      capturedAt: new Date().toISOString(),
      panel: 'ButtonColorDiagnostic',
      reading,
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 1500);
    } catch {
      // ignore
    }
  }, [reading]);

  if (!reading) {
    return (
      <div style={emptyStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <span style={{ fontWeight: 600, color: '#fbbf24' }}>Button Color Diagnostic</span>
        </div>
        <div style={{ color: '#9ca3af' }}>Select a button element on the canvas to inspect its color mapping resolution.</div>
      </div>
    );
  }

  const verdictColor =
    reading.verdictSeverity === 'ok' ? '#4ade80' : reading.verdictSeverity === 'warning' ? '#fbbf24' : '#ef4444';

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontWeight: 700, color: '#fbbf24' }}>Button Color Diagnostic</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={refresh} title="Refresh" style={btnStyle}>
            <RefreshCw size={12} />
          </button>
          <button
            onClick={handleCopy}
            title="Copy as JSON"
            style={{
              ...btnStyle,
              background: copyState === 'copied' ? 'rgba(34, 197, 94, 0.2)' : 'transparent',
              color: copyState === 'copied' ? '#4ade80' : '#9ca3af',
            }}
          >
            <Copy size={12} />
            <span style={{ fontSize: 10, marginLeft: 4 }}>{copyState === 'copied' ? 'COPIED' : 'COPY'}</span>
          </button>
        </div>
      </div>

      <div style={verdictBoxStyle(verdictColor)}>
        <span style={{ color: verdictColor, fontWeight: 700 }}>VERDICT: </span>
        <span style={{ color: '#e5e7eb' }}>{reading.verdict}</span>
      </div>

      <SectionHeader>Element Identity</SectionHeader>
      <Row label="id" value={reading.elementId.slice(0, 8) + '...'} />
      <Row label="name" value={reading.elementName} />
      <Row label="type" value={reading.elementType} />
      <Row label="storeBackgroundColor" value={reading.storeBackgroundColor ?? '(none)'} />

      <SectionHeader>Edit-As Context</SectionHeader>
      <Row label="editAsBrandId" value={reading.editAsBrandId ?? '(OFF)'} highlight={!reading.editAsBrandId} />
      <Row label="editAsBrandData present" value={reading.editAsBrandDataPresent} />
      <Row label="total keys" value={reading.editAsBrandDataKeyCount} />
      <Row label="sm.sm_schools.* keys" value={reading.smSchoolsKeys.length} />
      <Row
        label="primary_color value"
        value={reading.smSchoolsPrimaryColor ?? '(not in data)'}
        highlight={!reading.smSchoolsPrimaryColor && !!reading.editAsBrandId}
      />
      {reading.smSchoolsKeys.length > 0 && reading.smSchoolsKeys.length <= 8 && (
        <div style={{ paddingLeft: 8, color: '#9ca3af', fontSize: 10, marginTop: 2 }}>
          {reading.smSchoolsKeys.map((k, i) => (
            <div key={i}>{k}</div>
          ))}
        </div>
      )}

      <SectionHeader>Element Mappings</SectionHeader>
      <Row label="has mappings" value={reading.hasMappingsForElement} />
      <Row label="total mappings" value={reading.allMappingsForElement.length} />
      <Row
        label="buttonFillColor mapping"
        value={reading.buttonFillColorMapping ? 'PRESENT' : 'MISSING'}
        highlight={!reading.buttonFillColorMapping}
      />
      {reading.buttonFillColorMapping && (
        <div style={{ paddingLeft: 8, color: '#e5e7eb', fontSize: 10, marginTop: 2 }}>
          <div>source: {reading.buttonFillColorMapping.source}</div>
          <div>table: {reading.buttonFillColorMapping.tableName}</div>
          <div>column: {reading.buttonFillColorMapping.columnName}</div>
          <div>fallback: {reading.buttonFillColorMapping.fallbackValue ?? '(none)'}</div>
        </div>
      )}

      <SectionHeader>Resolution Chain</SectionHeader>
      <Row label="lookup key" value={reading.constructedLookupKey ?? '(no mapping)'} />
      <Row
        label="key found in brand data"
        value={reading.lookupKeyFoundInBrandData}
        highlight={!!reading.constructedLookupKey && !reading.lookupKeyFoundInBrandData}
      />
      <Row label="key value" value={String(reading.lookupKeyValue ?? '(null)')} />
      <Row label="resolveMappedValue result" value={reading.resolvedValue ?? '(null)'} highlight={!reading.resolvedValue} />
      <Row label="final fill will be" value={reading.finalFillWillBe} />

      <SectionHeader>Fabric Object State</SectionHeader>
      <Row label="group found on canvas" value={reading.fabricGroupFound} />
      <Row label="_csButtonBg ref present" value={reading.fabricBgRefPresent} highlight={!reading.fabricBgRefPresent} />
      <Row label="live bg.fill" value={reading.fabricBgFillLive ?? '(bg ref not found)'} />
      <Row
        label="matches resolved value"
        value={reading.fabricBgMatchesResolved}
        highlight={!reading.fabricBgMatchesResolved && reading.resolvedValue !== null}
      />
    </div>
  );
}

const containerStyle: React.CSSProperties = {
  background: 'rgba(15, 15, 18, 0.97)',
  color: '#e5e7eb',
  border: '1px solid rgba(255, 255, 255, 0.14)',
  borderRadius: 10,
  padding: '12px 14px',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 11,
  maxHeight: '70vh',
  overflowY: 'auto',
};

const emptyStyle: React.CSSProperties = {
  background: 'rgba(20, 20, 24, 0.96)',
  color: '#e5e7eb',
  border: '1px solid rgba(255, 255, 255, 0.12)',
  borderRadius: 8,
  padding: '10px 14px',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 11,
};

const btnStyle: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid rgba(255,255,255,0.15)',
  color: '#9ca3af',
  cursor: 'pointer',
  padding: '2px 6px',
  borderRadius: 4,
  display: 'flex',
  alignItems: 'center',
};

const verdictBoxStyle = (color: string): React.CSSProperties => ({
  border: `1px solid ${color}`,
  background: `${color}15`,
  padding: '6px 8px',
  borderRadius: 4,
  marginBottom: 8,
  fontSize: 11,
  lineHeight: 1.4,
});

function SectionHeader({ children }: { children: string }) {
  return (
    <div style={{ marginTop: 10, marginBottom: 4, color: '#fbbf24', fontWeight: 700, fontSize: 10, letterSpacing: 0.5, textTransform: 'uppercase' }}>
      {children}
    </div>
  );
}

function Row({ label, value, highlight }: { label: string; value: string | number | boolean | null | undefined; highlight?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', borderBottom: '1px dotted rgba(255,255,255,0.06)' }}>
      <span style={{ color: '#9ca3af' }}>{label}</span>
      <span
        style={{
          color: highlight ? '#fbbf24' : '#e5e7eb',
          fontWeight: highlight ? 600 : 400,
          marginLeft: 12,
          textAlign: 'right',
          wordBreak: 'break-all',
        }}
      >
        {value === null || value === undefined ? '\u2014' : String(value)}
      </span>
    </div>
  );
}
